import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { Client, Mountpoint, PositionsSnapshot } from "@/types/api";

const KEY = "mountpoints";

export function useMountpoints(options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: [KEY],
    queryFn: async () => (await api.get<Mountpoint[]>("/api/mountpoints")).data,
    refetchInterval: options.refetchInterval,
  });
}

export function useMountpoint(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: async () => (await api.get<Mountpoint>(`/api/mountpoints/${id}`)).data,
    enabled: !!id,
  });
}

export function useMountpointClients(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, id, "clients"],
    queryFn: async () => (await api.get<Client[]>(`/api/mountpoints/${id}/clients`)).data,
    enabled: !!id,
  });
}

export interface CreateMountpointInput {
  name: string;
  format: string;
  description?: string;
  source_username: string;
  source_password: string;
}

export function useCreateMountpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateMountpointInput) =>
      (await api.post<Mountpoint>("/api/mountpoints", input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export interface UpdateMountpointInput {
  id: string;
  description?: string | null;
  format?: string;
  source_username?: string;
  source_password?: string;
}

export function useUpdateMountpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateMountpointInput) =>
      (await api.patch<Mountpoint>(`/api/mountpoints/${id}`, patch)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY, vars.id] });
    },
  });
}

export function useDeleteMountpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/mountpoints/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useToggleMountpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enable }: { id: string; enable: boolean }) =>
      (
        await api.post<Mountpoint>(`/api/mountpoints/${id}/${enable ? "enable" : "disable"}`)
      ).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY, vars.id] });
    },
  });
}

/** REST snapshot of the live map registry — lets list pages tell online / offline / sin posición. */
export function usePositionsSnapshot(options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ["positions", "snapshot"],
    queryFn: async () => (await api.get<PositionsSnapshot>("/api/positions")).data,
    refetchInterval: options.refetchInterval ?? 10_000,
  });
}
